import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Download, Eye, Mail, Phone, Building2, 
  Linkedin, Calendar, Clock, Globe, Monitor, Smartphone,
  MapPin, RefreshCw, Filter, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, MessageSquare, MoreVertical, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuTrigger, DropdownMenuSeparator 
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HRLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ats_token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const res = await fetch(`${API}/api/hr-signup/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLeads(data.signups || []);
      setTotal(data.total || 0);
      setStats(data.stats || { total: 0, new: 0, contacted: 0, converted: 0 });
    } catch (err) {
      toast.error('Failed to fetch HR leads');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const totalPages = Math.ceil(total / pageSize);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/hr-signup/${leadId}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) throw new Error('Failed to update status');
      toast.success('Status updated');
      fetchLeads();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/hr-signup/${leadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Lead deleted');
      fetchLeads();
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Job Title', 'Industry', 'Company Size', 'Status', 'Date', 'Location', 'Device'];
    const rows = leads.map(l => [
      `${l.first_name} ${l.last_name || ''}`.trim(),
      l.email,
      l.phone || '',
      l.company_name || '',
      l.job_title || '',
      l.industry || '',
      l.company_size || '',
      l.status || 'new',
      l.created_at ? new Date(l.created_at).toLocaleString() : '',
      l.location?.city ? `${l.location.city}, ${l.location.country}` : '',
      l.device_info?.device_type || ''
    ]);
    
    const csvContent = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hr_leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-yellow-100 text-yellow-700';
      case 'qualified': return 'bg-purple-100 text-purple-700';
      case 'converted': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'Mobile') return <Smartphone className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const openDetails = (lead) => {
    setSelectedLead(lead);
    setDetailsOpen(true);
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="hr-leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            HR Leads
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage HR professionals who signed up via the landing page</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="refresh-leads">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-leads">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-800">{stats.total || total}</p>
            <p className="text-sm text-slate-500">Total Leads</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{stats.new || 0}</p>
            <p className="text-sm text-slate-500">New</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-yellow-600">{stats.contacted || 0}</p>
            <p className="text-sm text-slate-500">Contacted</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-600">{stats.converted || 0}</p>
            <p className="text-sm text-slate-500">Converted</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, company..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="search-leads"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium text-slate-600">Lead</th>
                  <th className="text-left p-3 font-medium text-slate-600">Company</th>
                  <th className="text-left p-3 font-medium text-slate-600">Contact</th>
                  <th className="text-left p-3 font-medium text-slate-600">Location</th>
                  <th className="text-left p-3 font-medium text-slate-600">Device</th>
                  <th className="text-left p-3 font-medium text-slate-600">Status</th>
                  <th className="text-left p-3 font-medium text-slate-600">Date</th>
                  <th className="text-center p-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading leads...
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-slate-500">
                      No HR leads found
                    </td>
                  </tr>
                ) : leads.map((lead) => (
                  <tr key={lead.id} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-slate-800">{lead.first_name} {lead.last_name}</p>
                        <p className="text-xs text-slate-500">{lead.job_title || 'No title'}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-slate-400" />
                        <span>{lead.company_name || '-'}</span>
                      </div>
                      <p className="text-xs text-slate-500">{lead.industry || ''}</p>
                    </td>
                    <td className="p-3">
                      <div className="space-y-1">
                        <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </a>
                        {lead.phone && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {lead.phone}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {lead.location ? (
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          <MapPin className="w-3 h-3" />
                          <span>{lead.location.city || ''}{lead.location.city && lead.location.country ? ', ' : ''}{lead.location.country || ''}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      {lead.device_info ? (
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          {getDeviceIcon(lead.device_info.device_type)}
                          <span>{lead.device_info.device_type || 'Unknown'}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Select value={lead.status || 'new'} onValueChange={(v) => updateLeadStatus(lead.id, v)}>
                        <SelectTrigger className="h-7 w-28 text-xs">
                          <Badge className={`${getStatusColor(lead.status || 'new')} text-xs`}>
                            {lead.status || 'new'}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      <div className="text-xs text-slate-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                        </div>
                        <div className="flex items-center gap-1 text-slate-400">
                          <Clock className="w-3 h-3" />
                          {lead.created_at ? new Date(lead.created_at).toLocaleTimeString() : ''}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetails(lead)}>
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`mailto:${lead.email}`, '_blank')}>
                            <Mail className="w-4 h-4 mr-2" /> Send Email
                          </DropdownMenuItem>
                          {lead.linkedin && (
                            <DropdownMenuItem onClick={() => window.open(lead.linkedin, '_blank')}>
                              <Linkedin className="w-4 h-4 mr-2" /> View LinkedIn
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => deleteLead(lead.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-t flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * pageSize) + 1} - {((currentPage - 1) * pageSize) + leads.length} of {total} leads
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {currentPage} of {totalPages || 1}</span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lead Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>Complete information about this HR lead</DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Name</p>
                  <p className="font-medium">{selectedLead.first_name} {selectedLead.last_name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Email</p>
                  <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">{selectedLead.email}</a>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">Phone</p>
                  <p>{selectedLead.phone || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-500 uppercase">LinkedIn</p>
                  {selectedLead.linkedin ? (
                    <a href={selectedLead.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View Profile</a>
                  ) : <p>-</p>}
                </div>
              </div>

              {/* Company Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Company Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Company</p>
                    <p className="font-medium">{selectedLead.company_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Job Title</p>
                    <p>{selectedLead.job_title || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Industry</p>
                    <p>{selectedLead.industry || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Company Size</p>
                    <p>{selectedLead.company_size || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Message */}
              {selectedLead.message && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Message
                  </h4>
                  <p className="text-sm text-amber-700">{selectedLead.message}</p>
                </div>
              )}

              {/* Technical Details */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                  <Monitor className="w-4 h-4" /> Technical Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">IP Address</p>
                    <p className="font-mono">{selectedLead.ip_address || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Device Type</p>
                    <p>{selectedLead.device_info?.device_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Browser</p>
                    <p>{selectedLead.device_info?.browser || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Operating System</p>
                    <p>{selectedLead.device_info?.os || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Screen Resolution</p>
                    <p>{selectedLead.device_info?.screen_resolution || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Timezone</p>
                    <p>{selectedLead.device_info?.timezone || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Location */}
              {selectedLead.location && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Location
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-slate-500">City</p>
                      <p>{selectedLead.location.city || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Region</p>
                      <p>{selectedLead.location.region || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Country</p>
                      <p>{selectedLead.location.country || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Coordinates</p>
                      <p className="font-mono text-xs">
                        {selectedLead.location.latitude && selectedLead.location.longitude 
                          ? `${selectedLead.location.latitude}, ${selectedLead.location.longitude}` 
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="flex justify-between text-xs text-slate-500 pt-4 border-t">
                <span>Created: {selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleString() : '-'}</span>
                <span>Updated: {selectedLead.updated_at ? new Date(selectedLead.updated_at).toLocaleString() : '-'}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRLeadsPage;
